import { Info } from "@icons";
import classNames from "classnames";
import { ReactNode, useState } from "react";

interface FieldLabelProps {
  /** Id of the control this labels (click-to-focus). */
  htmlFor?: string;
  label?: ReactNode;
  required?: boolean;
  /** Description revealed by the (i) toggle. */
  info?: ReactNode;
  /** Start the description popover open. */
  infoOpen?: boolean;
  /** Actions rendered at the right end of the label row (e.g. a prefilled-value indicator). */
  labelActions?: ReactNode;
  /**
   * Keep the label for assistive tech but hide it visually — for surfaces that
   * already print the field's name next to the control.
   */
  labelHidden?: boolean;
}

/**
 * The shared label row for every form control: the label text, an optional
 * required marker, an (i) button that toggles a description popover (when
 * `info` is supplied), and an optional actions slot at the right end.
 */
export function FieldLabel({
  htmlFor,
  label,
  required = false,
  info,
  infoOpen = false,
  labelActions,
  labelHidden = false,
}: FieldLabelProps) {
  const [showInfo, setShowInfo] = useState(infoOpen);

  if (!label && !info && !labelActions) return null;

  const labelElement = label && (
    <label
      htmlFor={htmlFor}
      className={
        labelHidden ? "sr-only" : "text-[0.8125rem] font-medium text-default"
      }
    >
      {label}
      {required && (
        <span aria-hidden="true" className="text-danger ml-0.5">
          *
        </span>
      )}
    </label>
  );

  if (labelHidden && !info && !labelActions) return <>{labelElement}</>;

  return (
    <div
      className={classNames(
        "flex items-center gap-[7px] mb-1.5",
        labelActions ? "min-h-target" : "min-h-[20px]",
      )}
    >
      {labelElement}
      {info && (
        <span className="relative inline-flex">
          <button
            type="button"
            aria-label={
              typeof label === "string" ? `About ${label}` : "More information"
            }
            aria-expanded={showInfo}
            data-target-gap="field-info"
            onClick={() => setShowInfo((open) => !open)}
            className={classNames(
              "relative hit-target-min grid place-items-center w-5 h-5 shrink-0 rounded-full border-0 p-0 cursor-pointer transition-colors",
              {
                "text-muted hover:text-primary hover:bg-tint": !showInfo,
                "text-primary bg-tint": showInfo,
              },
            )}
          >
            <Info size={15} />
          </button>
          {showInfo && (
            <span
              role="tooltip"
              className="absolute top-[calc(100%+9px)] -left-2 z-30 w-[262px] px-[13px] py-[11px] rounded-md border border-divider bg-page shadow-lg text-[0.8125rem] leading-normal text-muted"
            >
              <span
                aria-hidden="true"
                className="absolute -top-[5px] left-[14px] w-[9px] h-[9px] rotate-45 border-l border-t border-divider bg-page"
              />
              {info}
            </span>
          )}
        </span>
      )}
      {labelActions && (
        <div className="flex items-center gap-3 ml-auto">{labelActions}</div>
      )}
    </div>
  );
}

export default FieldLabel;
